<template>
    <card v-if="group && room" class="bg-light rounded-0" :class="(displayRates && !displayQuote) ? 'opacity-75 disabled' : null">
        <template #default>
            <div class="d-block d-md-flex">
                <lazy-image v-if="displayImage" :src="displayImage" :background="true" :width="imageWidth" :height="imageHeight" class="flex-shrink-0 clickable" @click="showGallery=true" />
                <card-body class="flex-grow-1">
                    <property-card-icons :property="room" :useBedsCount="true" class="x-small mb-2"/>
                    <div v-if="room.id != hotel.id" class="mb-2 small text-primary">
                        {{ numRoomsAvailable || 'no available' }} {{ hotelRoomsLabel }} | <router-link :to="`/properties/${room.slug || room.id}`">View {{ roomLabel(1, true) }} details</router-link>
                    </div>
                    <card-title>{{ name }}</card-title>
                    <div v-if="room.summary" class="text-wrap-pre-line small text-muted">
                        {{ room.summary }}
                    </div>
                </card-body>
                <card-body class="ms-0 ms-md-auto d-block d-md-flex align-items-center pt-0 pt-md-3">
                    <template v-if="inquireOnly">
                        <ui-button class="rounded-2" @click="showInquiryModal=true" :block="true">
                            Inquire for rates
                        </ui-button>
                    </template>

                    <template v-else-if="displayRates">
                        <div v-if="displayQuote" class="d-flex d-md-block justify-content-between align-items-center">
                            <div>
                                <div class="text-nowrap">
                                    <h2 class="me-1 d-inline">{{ money(displayQuote.total) }}</h2>
                                    <span>/stay</span>
                                </div>
                                <div v-if="displayQuote.deposit" class="x-small text-muted">
                                    Plus a refundable security deposit of {{ money(displayQuote.deposit) }}
                                </div>
                            </div>

                            <ui-button @click="reserveButtonClick" color="success" :block="!uiStore.isMobile" size="sm" class="mt-2 rounded-2 text-nowrap">
                                {{ reserveOrRequestLable }}
                            </ui-button>
                        </div>
                        <div v-else>
                            Not available
                        </div>
                    </template>

                    <template v-else>
                        <div v-if="room.schedulePriceAvg" class="mb-lg-3 text-nowrap">
                            <span class="me-2 d-md-block d-lg-inline">Avg. </span><h4 class="me-1 d-inline">{{ money(room.schedulePriceAvg) }}</h4>/night
                        </div>
                        <h4 v-else class="mb-lg-3">
                            Inquire for rates
                        </h4>
                    </template>
                </card-body>
            </div>

            <public-property-inquire-modal v-if="showInquiryModal" :property="room" @close="showInquiryModal=false" />
            <vertical-gallery-modal v-if="showGallery" :media="galleryMedia" @close="showGallery=false">
                {{ name }}
            </vertical-gallery-modal>
        </template>
    </card>
</template>

<script type="text/javascript">

    import PropertyCardIcons from '@/components/Content/Public/Properties/Cards/Parts/Icons.vue';
    import PublicPropertyInquireModal from '@/components/Content/Public/Properties/InquireModal.vue';
    import CommonComponents from '@/components/Common.vue';
    import ApiObject from '@/classes/ApiObject.js';
    import Formatter from '@/classes/Formatter.js';
    import Utilities from '@/classes/Utilities.js';
    import Media from '@/classes/Media.js';

    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            PropertyCardIcons,
            PublicPropertyInquireModal,
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
            };
        },

        data() {

            return {
                showGallery: false,
                showInquiryModal: false,
            };
        },

        props: {
            hotel: {
                type: ApiObject,
                required: true,
            },

            group: {
                type: Object,
                required: true,
            },

            displayRates: {
                type: Boolean,
                default: false,
            },

            // quotes keyed by property ids
            quotes: {
                type: Object,
            },

        },

        emits: ['bookHotelRoom'],

        computed: {

            inquireOnly() {
                if ((this.room) && (this.room.status == 2)) {
                    return true;
                }

                return false;
            },

            imageWidth() {
                if (this.uiStore.isMobile) {
                    return '100%';
                }
                return '25%';
            },

            imageHeight() {
                if (this.uiStore.isMobile) {
                    return 100;
                }
                return 'auto';
            },
            

            numRoomsAvailable() {
                if (this.displayRates) {
                    let numAvailable = 0;

                    if (
                        (this.quotes) &&
                        (this.group) &&
                        (this.group.rooms) &&
                        (this.group.rooms.length)
                    ) {
                        for (let i=0; i<this.group.rooms.length; i++) {
                            if (this.quotes[this.group.rooms[i].id]) {
                                numAvailable++;
                            }
                        }
                    }

                    return numAvailable;
                }
                if (
                    (this.group) &&
                    (this.group.rooms)
                ) {
                    return this.group.rooms.length;
                }

                return 0;
            },

            displayQuote() {
                if (
                    (this.quotes) &&
                    (this.quotes[this.room.id])
                ) {
                    return this.quotes[this.room.id];
                }

                return null;
            },

            hotelRoomsLabel() {
                return this.roomLabel(this.numRoomsAvailable);
            },

            room() {
                let matchId = this.group.displayId || this.group.mainRoomId;
                if (matchId) {
                    for (let i=0; i<this.group.rooms.length; i++) {
                        if (matchId == this.group.rooms[i].id) {
                            return this.group.rooms[i];
                        }
                    }
                }
                else if (this.group.rooms.length) {
                    return this.group.rooms[0];
                }
                return null;
            },

            displayImage() {
                if ((this.room) && (this.room.photos) && (this.room.photos.length)) {
                    return this.room.photos[0].galleryThumbSource;
                }

                return null;
            },

            name() {
                if (
                    (this.group.group) &&
                    (this.group.group.name)
                ) {
                    return this.group.group.name;
                }
                else if (this.room) {
                    return this.room.name;
                }
                return null;
            },

            galleryMedia() {
                let gallery = [];

                if (
                    (this.room) &&
                    (this.room.firstSlideVideo)
                ) {
                    let thumb = null;
                    if (
                        (this.room) &&
                        (this.room.photos) &&
                        (this.room.photos.length)
                    ) {
                        thumb = this.room.photos[0].galleryThumbSource;
                    }

                    let media = new Media(thumb, this.room.firstSlideVideo.embed, null, 'video');
                    media.name = 'Video'
                    gallery.push(media);
                }

                if (
                    (this.room) &&
                    (this.room.first3dTour)
                ) {
                    let media = new Media(null, this.room.first3dTour.embed, null, 'tour');
                    media.name = '3D Tour';
                    gallery.push(media);
                }

                if (
                    (this.room) &&
                    (this.room.photos) &&
                    (this.room.photos.length)
                ) {
                    
                    for (let i=0; i<this.room.photos.length; i++) {
                        let thumb = this.room.photos[i].galleryThumbSource;
                        let src = this.room.photos[i].gallerySource || thumb;

                        let media = new Media(thumb, src, null, 'image');
                        media.title = this.room.photos[i].title;
                        media.description = this.room.photos[i].content;
                        gallery.push(media);
                    }
                    
                }

                if (
                    (this.room) &&
                    (this.room.publicExternalResources) &&
                    (this.room.publicExternalResources.length)
                ) {
                    for (let i=0; i<this.room.publicExternalResources.length; i++) {
                        let thumb = null;
                        let src = this.room.publicExternalResources[i].url;

                        let media = new Media(thumb, src);
                        media.title = this.room.publicExternalResources[i].name;
                        media.description = this.room.publicExternalResources[i].description;
                        media.data = {
                            floorPlan: true,
                        };
                        gallery.push(media);
                    }
                }

                if (gallery.length) {
                    return gallery;
                }

                return null;

            },

            reserveOrRequestLable() {
                if (
                    (this.hotel) && 
                    (this.hotel.bookingRequest)
                ) {
                    return 'Request to Book';
                }
                return 'Reserve Now';
            },
        },

        methods: {
            money(amount) {
                return Formatter.money(amount, 0);
            },

            roomLabel(count, lowerCase) {
                let plural = 'Suites';
                let singular = 'Suite';

                if (
                    (this.hotel) &&
                    (this.hotel.hotelSettings) &&
                    (this.hotel.hotelSettings.roomsLabel)
                ) {
                    singular = Utilities.ucFirst(this.hotel.hotelSettings.roomsLabel);
                    plural = singular+'s';
                }

                if (lowerCase) {
                    return Formatter.pluralize(count, plural, singular).toLowerCase();
                }

                return Formatter.pluralize(count, plural, singular);
            },

            reserveButtonClick() {
                this.$emit('bookHotelRoom', this.room);
            },
        }
    };

</script>
